import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { Router } from '@angular/router';
import { Md5 } from 'ts-md5';
import { API_URL_USERS, APP_DATA, AppData, AUTHENTICATION_PROVIDER, AuthenticationProvider } from '@shared/config';
import { AuthenticationService } from './authentication.service';
import { BackendUserDetails, INTENDED_URL, UserDetails, UserOrganisationDetails, UserSecurityDetails } from '../auth.data';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { updatePassword, type UpdatePasswordInput } from 'aws-amplify/auth';
import * as Sentry from '@sentry/angular';

@Injectable({ providedIn: 'root' })
export class CognitoAuthenticationService implements AuthenticationService {
  private http = inject(HttpClient);
  private apiUrl = inject(API_URL_USERS);
  private appData = inject<AppData>(APP_DATA);
  private authenticationProvider = inject<AuthenticationProvider>(AUTHENTICATION_PROVIDER);
  private router = inject(Router);
  private rendererFactory = inject(RendererFactory2);
  private renderer: Renderer2;
  private sentryErrorHandler = Sentry.createErrorHandler({ showDialog: false });
  private amplifyConfigured = false;
  private temporaryUserId: number | undefined;

  private get deployedDomain(): string {
    return window.location.origin;
  }

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.configureAmplify();
  }

  private configureAmplify(): void {
    if (this.amplifyConfigured) {
      return;
    }

    const redirectUri = `${this.deployedDomain}/cognito-callback`;
    const signOutUri = `${this.deployedDomain}/welcome`;

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: this.authenticationProvider.POOL,
          userPoolClientId: this.authenticationProvider.CLIENT_ID,
          loginWith: {
            oauth: {
              domain: this.authenticationProvider.COGNITO_AUTHENTICATION_DOMAIN.replace('https://', ''),
              scopes: ['email', 'openid', 'aws.cognito.signin.user.admin'],
              redirectSignIn: [redirectUri],
              redirectSignOut: [signOutUri],
              responseType: 'code' // Authorization code flow with PKCE
            }
          }
        }
      }
    });

    this.amplifyConfigured = true;
  }

  public openCognitoLoginPage(): void {
    signInWithRedirect();
  }

  public async handleAuthCallbackAndRedirectToIntendedUrl(): Promise<void> {
    // Amplify automatically handles the callback and token exchange
    // We just need to wait for the auth session to be available
    const session = await fetchAuthSession();

    if (!session.tokens?.accessToken) {
      throw new Error('Authentication failed: Unable to establish session.');
    }

    this.clearPriorSessionData();

    this.storeUserSecurityDetails(session);

    await this.fetchAndStoreUserDetails();

    this.redirectAfterLogin();
  }

  private async fetchAndStoreUserDetails(): Promise<void> {
    const url = this.apiUrl + '/user';
    const response = await firstValueFrom(this.http.get<BackendUserDetails>(url));
    this.storeUserDetails(response);
  }

  private redirectAfterLogin(): void {
    const returnUrl = sessionStorage.getItem(INTENDED_URL);
    sessionStorage.removeItem(INTENDED_URL);
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else {
      this.router.navigate(['/']);
    }
  }

  private readonly userSessionKeys = [
    'user',
    'userSecurity',
    'userOrganisation',
    'userRoles',
    'uiSkinName',
    'baseOrgId',
    'amplify_authenticated'
  ] as const;

  /**
   * Wipe every user/role/org key so stale data from a prior session
   * can never bleed into the new one.
   */
  private clearPriorSessionData(): void {
    for (const key of this.userSessionKeys) {
      localStorage.removeItem(key);
    }
  }

  private storeUserSecurityDetails(session: { tokens?: { idToken?: { payload?: Record<string, unknown> } } }): void {
    const email = session.tokens?.idToken?.payload?.['email'] as string | undefined;

    const userToStore: UserSecurityDetails = {
      email: email || null,
      gravatar: email ? this.getUserGravatarPath(email) : null
    };

    localStorage.setItem('userSecurity', JSON.stringify(userToStore));
  }

  public async getAccessTokenAsync(): Promise<string> {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        return session.tokens.accessToken.toString();
      }
      return '';
    } catch (error) {
      console.error('Error getting access token:', error);
      return '';
    }
  }

  public isAuthenticatedQuickCheck(): boolean {
    return localStorage.getItem('amplify_authenticated') === 'true';
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      const isAuth = !!session.tokens?.accessToken;
      localStorage.setItem('amplify_authenticated', isAuth ? 'true' : 'false');
      return isAuth;
    } catch {
      localStorage.setItem('amplify_authenticated', 'false');
      return false;
    }
  }

  storeUserDetails(user: BackendUserDetails) {
    const uiSkinName = 'idefigo';
    this.renderer.setAttribute(document.documentElement, 'ui-skin', uiSkinName);
    localStorage.setItem('uiSkinName', uiSkinName);

    const userToStore: UserDetails = {
      displayName: user.displayName,
      firstName: user.firstName,
      lastName: user.lastName,
      locale: user.attributes?.locale,
      timezone: user.attributes?.timezone,
      description: user.displayName,
      id: user.id
    };
    const userOrganisationToStore: UserOrganisationDetails = { name: user.orgName, id: user.orgId };
    const userRoles = user.roles;

    localStorage.setItem('user', JSON.stringify(userToStore));
    localStorage.setItem('userOrganisation', JSON.stringify(userOrganisationToStore));
    localStorage.setItem('userRoles', JSON.stringify(userRoles));
    localStorage.setItem('amplify_authenticated', 'true');

    if (this.appData.ORG_ID) {
      localStorage.setItem('baseOrgId', this.appData.ORG_ID);
    } else {
      localStorage.setItem('baseOrgId', JSON.stringify(userOrganisationToStore.id));
    }
  }

  public getUserSecurityDetails(): UserSecurityDetails {
    return JSON.parse(localStorage.getItem('userSecurity') || '{}');
  }

  public getUserDetails(): UserDetails {
    return JSON.parse(localStorage.getItem('user') || '{}');
  }

  public getUserOrganisationDetails(): UserOrganisationDetails {
    return JSON.parse(localStorage.getItem('userOrganisation') || '{}');
  }

  public getUserGravatarPath(userEmail?: string): string {
    if (!userEmail) {
      return 'https://www.gravatar.com/avatar/?d=mp';
    }
    userEmail = userEmail.trim().toLowerCase();
    const userEmailHash = Md5.hashStr(userEmail);
    return 'https://www.gravatar.com/avatar/' + userEmailHash + '?d=mp';
  }

  public hasRole(role: string): boolean {
    if (localStorage.getItem('userRoles')) {
      const rolesStr = localStorage.getItem('userRoles');
      if (rolesStr !== null) {
        const roles: Array<string> | null = JSON.parse(rolesStr);
        if (roles?.includes(role)) {
          return true;
        }
      }
    }
    return false;
  }

  public getUserRoles(): Array<string> {
    const rolesStr = localStorage.getItem('userRoles');
    if (rolesStr) {
      return JSON.parse(rolesStr);
    }
    return [];
  }

  private loggingOut = false;

  public async logout(): Promise<void> {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;

    // If there's no access token, the backend call would be unauthenticated and
    // the auth interceptor would recursively re-trigger logout(). Skip straight
    // to the Cognito/redirect flow and log a breadcrumb so the silent path is
    // still visible in telemetry.
    const token = await this.getAccessTokenAsync();
    if (!token) {
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: 'logout: no access token present, skipping backend logout'
      });
      this.cognitoLogout();
      return;
    }

    this.backendLogout();
  }

  /**
   * Returns true for keys that Amplify/Cognito requires to exist in order to
   * execute signOut (token revocation + hosted-UI redirect). These keys must
   * NOT be removed before calling signOut().
   */
  private isCognitoSignOutKey(key: string): boolean {
    return key.startsWith('CognitoIdentityServiceProvider.') || key.startsWith('amplify-signin-with-hostedUI');
  }

  /**
   * Removes every entry in `storage` whose key is NOT required by Cognito for
   * signOut, and returns a snapshot of what was removed so it can be restored
   * on failure.
   */
  private clearNonCognitoStorage(storage: Storage): Record<string, string> {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)!;
      if (!this.isCognitoSignOutKey(key)) {
        keysToRemove.push(key);
      }
    }

    const cleared: Record<string, string> = {};
    for (const key of keysToRemove) {
      cleared[key] = storage.getItem(key)!;
      storage.removeItem(key);
    }
    return cleared;
  }

  private cognitoLogout(): void {
    // Clear all non-Cognito storage before signOut so app state is fresh before Amplify's automatic redirect fires.
    this.temporaryUserId = this.getUserDetails().id;
    this.clearNonCognitoStorage(localStorage);
    this.clearNonCognitoStorage(sessionStorage);

    let fallbackRedirected = false;
    // Manual fallback redirect for the failure paths only. On the happy path
    // we deliberately do NOT navigate ourselves: Amplify's signOut() with
    // hosted-UI configured already sets window.location.href to Cognito's
    // /logout endpoint, which is what actually kills the session cookie on
    // the auth domain. If we override that navigation, the cookie survives
    // and the next signInWithRedirect() silently SSO's the user straight
    // back in without showing the hosted-UI login form.
    const fallbackRedirectToWelcome = () => {
      if (fallbackRedirected) {
        return;
      }
      fallbackRedirected = true;
      try {
        localStorage.clear();
        sessionStorage.clear();
        this.resetAmplifyInMemoryState();
      } finally {
        window.location.href = '/welcome';
      }
    };

    // Guard against signOut() hanging (e.g. hosted-UI endpoint unreachable)
    // so we always leave the page even if Amplify never settles. Report to
    // Sentry so silent Cognito outages are still visible in telemetry.
    const signOutTimeout = setTimeout(() => {
      Sentry.withScope((scope) => {
        scope.setContext('cognito_logout_timeout', {
          cxo_backend_user_id: this.temporaryUserId ?? 'unknown'
        });
        Sentry.captureMessage('Cognito signOut() timed out after 5s, forcing redirect', 'warning');
      });
      fallbackRedirectToWelcome();
    }, 5000);

    signOut({ global: true }).catch((error) => {
      clearTimeout(signOutTimeout);
      Sentry.withScope((scope) => {
        scope.setContext('cognito_logout_failed', {
          cxo_backend_user_id: this.temporaryUserId ?? 'unknown'
        });
        this.sentryErrorHandler.handleError(error);
      });
      // Only on failure do we navigate manually – on success we must let
      // Amplify's redirect to Cognito's /logout endpoint complete so the
      // hosted-UI session cookie is actually cleared.
      fallbackRedirectToWelcome();
    });
  }

  /**
   * Force Amplify to re-read its (now-empty) storage, discarding any
   * cached tokens that were held in memory from the previous session.
   */
  private resetAmplifyInMemoryState(): void {
    this.amplifyConfigured = false;
    this.configureAmplify();
  }

  private backendLogout(): void {
    const url = this.apiUrl + '/auth/logout';
    // The caller (logout()) only reaches this path when a valid access token
    // exists, so we let the auth interceptor attach the bearer normally. That
    // preserves the server-side audit trail of who logged out.
    this.http
      .post(url, {})
      .pipe(timeout(8000)) // 8 seconds
      .subscribe({
        next: () => {
          this.cognitoLogout();
        },
        error: (error) => {
          Sentry.withScope((scope) => {
            scope.setContext('backend_logout_failed', {
              cxo_backend_user_id: this.getUserDetails().id ?? 'unknown'
            });
            this.sentryErrorHandler.handleError(error);
          });

          this.cognitoLogout();
        }
      });
  }

  public async changePassword({ oldPassword, newPassword }: UpdatePasswordInput): Promise<boolean> {
    try {
      await updatePassword({ oldPassword, newPassword });
      return true;
    } catch (err) {
      console.error('Error changing password:', err);
    }
    return false;
  }
}
